import React, {
  createContext,
  useContext, useEffect,
  useState,
} from 'react';
import {FormikProvider, useFormik} from "formik";
import debounce from "debounce";
import {
  getList,
  getFile,
  getAppraisalCities,
  getAppraisalStreets
} from '../actions/actions'
import {downloadFile} from "../../../common/utils/files/downloadFile";

const AppraisalsContext = createContext(null);

export const useAppraisalsContext = () => useContext(AppraisalsContext);

export const AppraisalsProvider = ({children}) => {
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  // main filters
  const [freeText, setFreeText] = useState('');
  const [type, setType] = useState(null);
  const [role, setRole] = useState(null);
  const [decidingAppraiser, setDecidingAppraiser] = useState(null);
  const [version, setVersion] = useState(null);
  const [block, setBlock] = useState('');
  const [parcel, setParcel] = useState('');
  const [localCommittee, setLocalCommittee] = useState(null);
  const [issueInDispute, setIssueInDispute] = useState(null);
  const [publicationDateFrom, setPublicationDateFrom] = useState(null);
  const [publicationDateTo, setPublicationDateTo] = useState(null);
  const [decisionDateFrom, setDecisionDateFrom] = useState(null);
  const [decisionDateTo, setDecisionDateTo] = useState(null);
  // secondary filters
  const [city, setCity] = useState(null);//address
  const [street, setStreet] = useState(null);
  const [neighborhood, setNeighborhood] = useState('');
  const [propertyType, setPropertyType] = useState(null);
  const [use, setUse] = useState('');
  const [designation, setDesignation] = useState('');
  const [area, setArea] = useState('');
  const [unitCount, setUnitCount] = useState('');
  const [floor, setFloor] = useState('');
  const [propertyCondition, setPropertyCondition] = useState('');
  const [constructionType, setConstructionType] = useState('');
  const [effectiveDateFrom, setEffectiveDateFrom] = useState(null);
  const [effectiveDateTo, setEffectiveDateTo] = useState(null);
  const [publicationDate, setPublicationDate] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [plan, setPlan] = useState('');

  const handleSearchChange = (key, handler) => value => {
    setPage(0);
    handler(value);
  };
  const handleSearchReset = (key, handler) => () => {
    handleSearchChange(key, handler)(null);
  };

  const handleCityChange = (value) => {
    setPage(0);
    setCity(value);
    handleSearchReset('street', setStreet)();
  };
  const handleCityReset = () => {
    handleSearchReset('city', setCity)();
    handleSearchReset('street', setStreet)();
    setStreets([])
  };

  const refreshGrid = debounce(async () => {
    const result = await getList({
      requestedPageNumber: page,
      city: city?.value ?? '',
      street: street?.value ?? '',
    });
    setData(result);
  }, 500);

  useEffect(() => {
    refreshGrid();
  }, [
    page,
    city,
    street,
    type,
    role,
    decidingAppraiser,
    version,
    block,
    parcel,
    localCommittee,
    issueInDispute
  ]);

  useEffect(() => {
    const loadCities = async () => {
      const cities = await getAppraisalCities();
      setCities(cities);
    };
    void loadCities();
  }, []);
  useEffect(() => {
    if (city) {
      const loadStreets = async () => {
        const streets = await getAppraisalStreets(city?.value);
        setStreets(streets);
      };

      void loadStreets();
    }
  }, [city])

  const formik = useFormik({
    initialValues: {
      freeText: '',
      city: null,
      street: null
    },
    onSubmit: () => {
    },
    enableReinitialize: true,
  });

  const handleDownload = async (id, fileName) => {
    const link = await getFile(id)
    if (link) downloadFile(link, fileName)
  };

  const value = {
    data,
    setData,
    page,
    setPage,
    cities,
    streets,
    search: {
      freeText: {
        value: freeText,
        onChange: handleSearchChange('freeText', setFreeText),
      },
      type: {
        value: type,
        onChange: handleSearchChange('type', setType),
        onReset: handleSearchReset('type', setType),
      },
      role: {
        value: role,
        onChange: handleSearchChange('role', setRole),
        onReset: handleSearchReset('role', setRole),
      },
      decidingAppraiser: {
        value: decidingAppraiser,
        onChange: handleSearchChange('decidingAppraiser', setDecidingAppraiser),
        onReset: handleSearchReset('decidingAppraiser', setDecidingAppraiser),
      },
      version: {
        value: version,
        onChange: handleSearchChange('version', setVersion),
        onReset: handleSearchReset('version', setVersion),
      },
      block: {
        value: block,
        onChange: handleSearchChange('block', setBlock),
      },
      parcel: {
        value: parcel,
        onChange: handleSearchChange('parcel', setParcel),
      },
      localCommittee: {
        value: localCommittee,
        onChange: handleSearchChange('localCommittee', setLocalCommittee),
        onReset: handleSearchReset('localCommittee', setLocalCommittee),
      },
      issueInDispute: {
        value: issueInDispute,
        onChange: handleSearchChange('issueInDispute', setIssueInDispute),
        onReset: handleSearchReset('issueInDispute', setIssueInDispute),
      },
      publicationDateFrom: {
        value: publicationDateFrom,
        onChange: handleSearchChange('publicationDateFrom', setPublicationDateFrom),
        onReset: handleSearchReset('publicationDateFrom', setPublicationDateFrom),
      },
      publicationDateTo: {
        value: publicationDateTo,
        onChange: handleSearchChange('publicationDateTo', setPublicationDateTo),
        onReset: handleSearchReset('publicationDateTo', setPublicationDateTo),
      },
      decisionDateFrom: {
        value: decisionDateFrom,
        onChange: handleSearchChange('decisionDateFrom', setDecisionDateFrom),
        onReset: handleSearchReset('decisionDateFrom', setDecisionDateFrom),
      },
      decisionDateTo: {
        value: decisionDateTo,
        onChange: handleSearchChange('decisionDateTo', setDecisionDateTo),
        onReset: handleSearchReset('decisionDateTo', setDecisionDateTo),
      },

      city: {
        value: city,
        onChange: handleCityChange,
        onReset: handleCityReset,
      },
      street: {
        value: street,
        onChange: handleSearchChange('street', setStreet),
        onReset: handleSearchReset('street', setStreet),
      },
      neighborhood: {
        value: neighborhood,
        onChange: handleSearchChange('neighborhood', setNeighborhood),
      },
      propertyType: {
        value: propertyType,
        onChange: handleSearchChange('propertyType', setPropertyType),
        onReset: handleSearchReset('propertyType', setPropertyType),
      },
      use: {
        value: use,
        onChange: handleSearchChange('use', setUse),
      },
      designation: {
        value: designation,
        onChange: handleSearchChange('designation', setDesignation),
      },
      area: {
        value: area,
        onChange: handleSearchChange('area', setArea),
      },
      unitCount: {
        value: unitCount,
        onChange: handleSearchChange('unitCount', setUnitCount),
      },
      floor: {
        value: floor,
        onChange: handleSearchChange('floor', setFloor),
      },
      propertyCondition: {
        value: propertyCondition,
        onChange: handleSearchChange('propertyCondition', setPropertyCondition),
      },
      constructionType: {
        value: constructionType,
        onChange: handleSearchChange('constructionType', setConstructionType),
      },
      effectiveDateFrom: {
        value: effectiveDateFrom,
        onChange: handleSearchChange('effectiveDateFrom', setEffectiveDateFrom),
        onReset: handleSearchReset('effectiveDateFrom', setEffectiveDateFrom),
      },
      effectiveDateTo: {
        value: effectiveDateTo,
        onChange: handleSearchChange('effectiveDateTo', setEffectiveDateTo),
        onReset: handleSearchReset('effectiveDateTo', setEffectiveDateTo),
      },
      publicationDate: {
        value: publicationDate,
        onChange: handleSearchChange('publicationDate', setPublicationDate),
        onReset: handleSearchReset('publicationDate', setPublicationDate),
      },
      purpose: {
        value: purpose,
        onChange: handleSearchChange('purpose', setPurpose),
      },
      plan: {
        value: plan,
        onChange: handleSearchChange('plan', setPlan),
      },
    },
    handleDownload
  };

  return (
    <AppraisalsContext.Provider value={value}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </AppraisalsContext.Provider>
  );
};